import React, { useState, useEffect, useCallback } from 'react';
import { naatKhawanService, adminService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { NaatKhawan } from '../../types';
import Pagination from '../common/Pagination';
import ConfirmationModal from '../common/ConfirmationModal';
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  X,
  Save,
  Users
} from 'lucide-react';
import './AdminNaatKhawans.css';

interface NaatKhawanForm {
  name: string;
  urduName: string;
  bio: string;
  birthDate: string;
  birthPlace: string;
}

const AdminNaatKhawans: React.FC = () => {
  const [naatKhawans, setNaatKhawans] = useState<NaatKhawan[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedNaatKhawan, setSelectedNaatKhawan] = useState<NaatKhawan | null>(null);
  const [naatKhawanForm, setNaatKhawanForm] = useState<NaatKhawanForm>({
    name: '',
    urduName: '',
    bio: '',
    birthDate: '',
    birthPlace: '',
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [naatKhawanToDelete, setNaatKhawanToDelete] = useState<NaatKhawan | null>(null);
  
  const { showToast } = useToast();

  // Load data on component mount
  const loadNaatKhawans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await naatKhawanService.getAll(currentPage, 20, searchTerm);
      if (response.success && response.data) {
        setNaatKhawans(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      showToast('Failed to load NaatKhawans', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, showToast]);

  useEffect(() => {
    loadNaatKhawans();
  }, [loadNaatKhawans]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const openAddModal = () => {
    setModalMode('add');
    setNaatKhawanForm({
      name: '',
      urduName: '',
      bio: '',
      birthDate: '',
      birthPlace: '',
    });
    setSelectedNaatKhawan(null);
    setShowModal(true);
  };

  const openEditModal = (naatKhawan: NaatKhawan) => {
    setModalMode('edit');
    setSelectedNaatKhawan(naatKhawan);
    setNaatKhawanForm({
      name: naatKhawan.name,
      urduName: naatKhawan.urduName || '',
      bio: naatKhawan.bio || '',
      birthDate: naatKhawan.birthDate || '',
      birthPlace: naatKhawan.birthPlace || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNaatKhawan(null);
    setNaatKhawanForm({
      name: '',
      urduName: '',
      bio: '',
      birthDate: '',
      birthPlace: '',
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!naatKhawanForm.name.trim()) {
      showToast('Name (English) is required', 'error');
      return;
    }
    
    setLoading(true);

    try {
      if (modalMode === 'add') {
        const response = await adminService.createNaatKhawan(naatKhawanForm);
        if (response.success) {
          showToast('NaatKhawan added successfully!', 'success');
          loadNaatKhawans();
          closeModal();
        } else {
          showToast(response.message || 'Failed to add NaatKhawan', 'error');
        }
      } else if (selectedNaatKhawan) {
        const response = await adminService.updateNaatKhawan(selectedNaatKhawan.id, naatKhawanForm);
        if (response.success) {
          showToast('NaatKhawan updated successfully!', 'success');
          loadNaatKhawans();
          closeModal();
        } else {
          showToast(response.message || 'Failed to update NaatKhawan', 'error');
        }
      }
    } catch (error) {
      showToast(`Failed to ${modalMode} NaatKhawan: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (naatKhawan: NaatKhawan) => {
    setNaatKhawanToDelete(naatKhawan);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!naatKhawanToDelete) return;
    
    try {
      const response = await adminService.deleteNaatKhawan(naatKhawanToDelete.id);
      if (response.success) {
        showToast('NaatKhawan deleted successfully!', 'success');
        loadNaatKhawans();
      }
    } catch (error) {
      showToast('Failed to delete NaatKhawan', 'error');
    } finally {
      setNaatKhawanToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setNaatKhawanToDelete(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="admin-naatkhawans">
      <div className="admin-naatkhawans-header">
        <h2 className="admin-section-title">
          <Users size={24} />
          Manage NaatKhawans
        </h2>
        <button
          className="admin-btn admin-btn-primary"
          onClick={openAddModal}
        >
          <Plus size={20} />
          Add New NaatKhawan
        </button>
      </div>

      <div className="admin-search-bar">
        <div className="admin-search-input-wrapper">
          <Search className="admin-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search NaatKhawans..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="admin-search-input"
          />
        </div>
      </div>

      {loading && (
        <div className="admin-loading">Loading NaatKhawans...</div>
      )}

      <div className="admin-naatkhawans-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Urdu Name</th>
              <th>Birth Date</th>
              <th>Birth Place</th>
              <th>Audio Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {naatKhawans.map((naatKhawan) => (
              <tr key={naatKhawan.id}>
                <td>
                  <div className="admin-naatkhawan-name">
                    <strong>{naatKhawan.name}</strong>
                  </div>
                </td>
                <td>
                  <div className="admin-urdu-name" dir="rtl">
                    {naatKhawan.urduName || 'N/A'}
                  </div>
                </td>
                <td>{formatDate(naatKhawan.birthDate || '')}</td>
                <td>{naatKhawan.birthPlace || 'N/A'}</td>
                <td>{naatKhawan.audioCount || 0}</td>
                <td>
                  <div className="admin-actions">
                    <button
                      className="admin-action-btn admin-action-edit"
                      onClick={() => openEditModal(naatKhawan)}
                      title="Edit"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      className="admin-action-btn admin-action-delete"
                      onClick={() => handleDelete(naatKhawan)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="admin-pagination"
        />
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{modalMode === 'add' ? 'Add New NaatKhawan' : 'Edit NaatKhawan'}</h3>
              <button
                className="admin-modal-close"
                onClick={closeModal}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="admin-modal-form">
              <div className="admin-form-group">
                <label>Name (English) *</label>
                <input
                  type="text"
                  value={naatKhawanForm.name}
                  onChange={(e) => setNaatKhawanForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter NaatKhawan's name in English"
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Name (Urdu) <span style={{color: '#888', fontSize: '0.9em'}}>(optional)</span></label>
                <input
                  type="text"
                  value={naatKhawanForm.urduName}
                  onChange={(e) => setNaatKhawanForm(prev => ({ ...prev, urduName: e.target.value }))}
                  dir="rtl"
                  placeholder="اختیاری - نعت خواں کا نام اردو میں"
                />
              </div>

              <div className="admin-form-group">
                <label>Biography <span style={{color: '#888', fontSize: '0.9em'}}>(optional)</span></label>
                <textarea
                  value={naatKhawanForm.bio}
                  onChange={(e) => setNaatKhawanForm(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Enter biography (optional)..."
                  rows={4}
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Birth Date <span style={{color: '#888', fontSize: '0.9em'}}>(optional)</span></label>
                  <input
                    type="date"
                    value={naatKhawanForm.birthDate}
                    onChange={(e) => setNaatKhawanForm(prev => ({ ...prev, birthDate: e.target.value }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Birth Place <span style={{color: '#888', fontSize: '0.9em'}}>(optional)</span></label>
                  <input
                    type="text"
                    placeholder="e.g., Lahore, Pakistan (optional)"
                    value={naatKhawanForm.birthPlace}
                    onChange={(e) => setNaatKhawanForm(prev => ({ ...prev, birthPlace: e.target.value }))}
                  />
                </div>
              </div>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={loading}
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : modalMode === 'add' ? 'Add NaatKhawan' : 'Update NaatKhawan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          onConfirm={handleConfirmDelete}
          onClose={handleCancelDelete}
          title="Delete NaatKhawan"
          message={`Are you sure you want to delete "${naatKhawanToDelete?.name}"? This will also delete all associated naats and cannot be undone.`}
          type="danger"
        />
      )}
    </div>
  );
};

export default AdminNaatKhawans;